import { RoleEnum, User } from '@/api';
import { dataModule } from '@/store/data';
import { computeFieldItems, mapObjectToSelectItem } from '@/common/utilsFilterSchema';
import FilterTagsToSelect from '@/components/common/filter/FilterTagsToSelect.vue';
import {
  configButtonGroup,
  configCardSelect,
  configCardInput,
  configCheckbox,
  configDateTimeRange,
  configDateTimeRangeSmall,
  configInput,
  configLicensePlateInput,
  configReturnIntervalSelect,
  configReturnIntervalSelectSmall,
  configSelect,
  configSelectAsTags,
  configSelectAsTagsSmall,
  configSelectSmall,
  configureDivider,
  SimpleFilterOptions,
  configMultipleInputSmall,
  configCardSelectSmall,
  configSelectAsTagsForCameras,
  configSelectAsTagsSmallForCameras,
  configureConfidence,
  configureRange,
  configButtonGroupNew,
  configUserSelect,
  configUserSelectSmall,
  configUserRealNameSelectSmall,
  configUserRealNameSelect
} from '@/components/common/filter/filters/types';
import { isDefined } from '@/definitions/services/helpers';
import { AuditLogsFilter } from '@/api/models/AuditLogsFilter';
import { CameraStatusColorCode } from '@/api/models/Status';
import { languageModule } from '@/store/languages';
import { IFormContext, IFormLayoutItem, IFormLayoutRow, IFormModel } from '@/uikit/forms/NForm.vue';
import { isStringWithValue } from '@/common/utils';
import FilterSmallRange from '@/components/common/filter/controls/FilterSmallRange.vue';
import { LineDirections } from '@/components/common/types';
import DistanceRange from '@/components/counters/DistanceRange.vue';
import { fetchAuditLogsObjectActionsMap, fetchAuditLogsObjectTypes, matchAuditLogsObjectTypesWithActions } from '@/pages/audit-logs/forms/utils';
import { addRowClasses, decodeScore, entcodeScore, generateLoadSearchMethodsByLVM, generateWatchListsLoadSearchMethods, generateWatchListsWithoutUnknownLoadSearchMethods, isValidIpV4Address, isValidIpV6Address, Positions } from './utils';
import { DateTimeRangeMode } from '@/uikit/datetime/NDateTimeRange.vue';
import { AnnexAlarmItemStatus } from '@/pages/annex/alarms/AnnexAlarmFilterBuilder';
import AnnexAlarmStatusHelperIcon from '@/pages/annex/alarms/AnnexAlarmStatusHelperIcon.vue';

export class CommonFilterSchemaModule {
  // COMMON EVENTS EPISODES

  getWatchListsWithUnmatched(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'matched_lists',
      i18n_label: 'common.watch_lists',
      i18n_placeholder: 'common.watch_lists',
      ...generateWatchListsLoadSearchMethods()
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions); // this._getBigMatchedList(resultOptions);
  }

  getMatchedLists(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'matched_lists',
      i18n_label: 'common.watch_lists',
      i18n_placeholder: 'common.watch_lists',
      ...generateWatchListsWithoutUnknownLoadSearchMethods()
    };
    const resultOptions = Object.assign(baseOptions, options);

    return options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions); // this._getBigMatchedList(resultOptions);
  }

  private _getBigMatchedList(options: SimpleFilterOptions): IFormLayoutItem {
    return {
      classes: 'heading-m n-form-item_fill n-form-label-horizontal-200 n-form-pad-10 n-form-label-text-align-right',
      component: FilterTagsToSelect,
      path: options.path,
      i18n_label: options.i18n_label,
      props: {
        i18n_placeholder: options.i18n_placeholder,
        items: options.items
      }
    };
  }

  getAcknowledged(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: options.path ?? 'acknowledged',
      anyItem: true,
      labeled: true,
      i18n_label: 'common.acknowledged',
      i18n_placeholder: 'common.acknowledged',
      items: [
        { value: 'False', i18n_label: 'common.only_unacknowledged' },
        { value: 'True', i18n_label: 'common.only_acknowledged' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getIsCaseParticipant(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'is_case_participant',
      anyItem: true,
      labeled: true,
      i18n_label: 'clusters.is_case_participant',
      i18n_placeholder: 'clusters.is_case_participant',
      items: [
        { value: 'False', i18n_label: 'clusters.only_clusters' },
        { value: 'True', i18n_label: 'clusters.only_participants' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getHasUnacknowledgedCaseClusters(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'has_unacknowledged_case_clusters',
      anyItem: true,
      labeled: true,
      i18n_label: 'common.unacknowledged_clusters',
      i18n_placeholder: 'common.unacknowledged_clusters',
      items: [
        { value: 'False', i18n_label: 'common.no_unacknowledged' },
        { value: 'True', i18n_label: 'common.has_unacknowledged' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getFaceBodyFilter(options: SimpleFilterOptions): IFormLayoutItem {
    const FaceBodyFilterItems = {
      HasFace: 'has_face',
      HasBody: 'has_body'
    } as const;
    const baseOptions: SimpleFilterOptions = {
      name: 'has_body_or_face',
      anyItem: true,
      labeled: true,
      i18n_label: 'common.face_body_filter_label',
      i18n_placeholder: 'common.face_body_filter_placeholder',
      checkForEmpty: function (item: any) {
        const result = item.has_face || item.has_body;
        return !result;
      },
      items: [
        { value: FaceBodyFilterItems.HasFace, i18n_label: 'common.faces' },
        { value: FaceBodyFilterItems.HasBody, i18n_label: 'common.bodies' }
      ]
    };
    function decode(this: IFormContext) {
      if (this.model.has_body) {
        return FaceBodyFilterItems.HasBody;
      } else if (this.model.has_face) {
        return FaceBodyFilterItems.HasFace;
      } else {
        return undefined;
      }
    }
    function encode(this: IFormContext, model: IFormModel, value: string) {
      this.model.has_body = undefined;
      this.model.has_face = undefined;

      if (value === FaceBodyFilterItems.HasBody) {
        this.model.has_body = true;
      } else if (value === FaceBodyFilterItems.HasFace) {
        this.model.has_face = true;
      }
    }

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions, null, encode, decode) : configButtonGroup(resultOptions, encode, decode);
  }

  getCameraGroups(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'camera_groups',
      i18n_label: 'common.camera_groups',
      i18n_placeholder: 'common.select_camera_group',
      ...generateLoadSearchMethodsByLVM(dataModule.cameraGroupsModule)
    };

    const handlers = {
      ['update:modelValue']: function (this: IFormContext, value: any) {
        if (value?.length && this.model.cameras?.length) this.model.cameras = [];
      }
    };

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions, handlers) : configSelectAsTags(resultOptions, handlers);
  }

  getCameras(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'cameras',
      i18n_label: 'common.cameras',
      i18n_placeholder: 'common.select_camera',
      ...generateLoadSearchMethodsByLVM(dataModule.camerasModule)
    };
    if (options.loadItems) {
      baseOptions.items = [];
    }
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions);
  }

  getTags(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'tags',
      i18n_label: 'common.tags',
      i18n_placeholder: 'common.tags'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions);
  }

  getCamerasDependedOnCameraGroups(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'cameras',
      i18n_label: 'common.cameras',
      i18n_placeholder: 'common.select_camera'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmallForCameras(resultOptions) : configSelectAsTagsForCameras(resultOptions);
  }

  getCamerasDependedOnCounter(options: SimpleFilterOptions,): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'cameras',
      i18n_label: 'common.cameras',
      i18n_placeholder: 'common.select_camera',
      ...generateLoadSearchMethodsByLVM(dataModule.camerasModule, { filters: { id_in: options.id_in } })
    };
    if (options.loadItems) {
      baseOptions.items = [];
    }
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions);
  }

  getRemoteMonitoringEventDateRange(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: 'common.date_and_time'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configDateTimeRangeSmall(resultOptions) : configDateTimeRange(resultOptions);
    // return options?.small ? configDateTimeRangeSmall(resultOptions, 'event_timestamp_gte', 'event_timestamp_lte') : configDateTimeRange(resultOptions, 'event_timestamp_gte', 'event_timestamp_lte');
  }

  getDateRange(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: 'common.date',
      mode: DateTimeRangeMode.Date
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configDateTimeRangeSmall(resultOptions) : configDateTimeRange(resultOptions);
  }

  getDateTimeRange(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: 'common.date_and_time'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configDateTimeRangeSmall(resultOptions) : configDateTimeRange(resultOptions);
  }

  getFirstClusterEventDateTimeRange(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      name: 'firstEventDateRange',
      i18n_label: 'clusters.first_event_date'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small
      ? configDateTimeRangeSmall(resultOptions, 'first_event_date_gte', 'first_event_date_lte')
      : configDateTimeRange(resultOptions, 'first_event_date_gte', 'first_event_date_lte');
  }

  getCreatedDateTimeRange(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: 'common.date_and_time'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small
      ? configDateTimeRangeSmall(resultOptions, 'created_date_gte', 'created_date_lte')
      : configDateTimeRange(resultOptions, 'created_date_gte', 'created_date_lte');
  }

  getClusterEventDateTimeRange(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      name: 'clusterEventDateRange',
      i18n_label: 'clusters.event_date'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small
      ? configDateTimeRangeSmall(resultOptions, 'event_date_gte', 'event_date_lte')
      : configDateTimeRange(resultOptions, 'event_date_gte', 'event_date_lte');
  }

  getVideoArchive(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'video_archives',
      i18n_label: 'common.video_archive',
      i18n_placeholder: 'common.select_video_archive',
      ...generateLoadSearchMethodsByLVM(dataModule.videosModule)
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions);
  }

  getVideoArchiveId(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'video_archive',
      i18n_label: 'common.video_archive_id',
      i18n_placeholder: 'common.enter_video_archive_id',
      multiple: true,
      inputAlphabet: '0123456789,'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getLocalStranger(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'local',
      i18n_label: 'common.local',
      anyItem: true,
      labeled: true,
      multiple: options.multiple,
      items: [
        { value: true, i18n_label: 'common.local_stranger_type_local' },
        { value: false, i18n_label: 'common.local_stranger_type_stranger' },
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getMatchedCardSelect(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'matched_card_in',
      i18n_label: 'common.card_name',
      i18n_placeholder: 'common.card_name',
      multiple: true
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options.small ? configCardSelectSmall(resultOptions) : configCardSelect(resultOptions);
  }

  getCardSelect(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'card',
      i18n_label: 'common.card_name',
      i18n_placeholder: 'common.select_card'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options.small ? configCardSelectSmall(resultOptions) : configCardSelect(resultOptions);
  }

  // EVENTS
  getMatches(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'no_match',
      i18n_label: 'common.matches',
      i18n_placeholder: 'common.matches',
      anyItem: true,
      labeled: true,
      closeIcon: false,
      items: [
        { value: 'False', i18n_label: 'common.only_with_matches' },
        { value: 'True', i18n_label: 'common.only_without_matches' }
      ]
    };

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getMatchesWithConfidence(options: SimpleFilterOptions): IFormLayoutRow {
    const matchesOptions = {
      ...addRowClasses(options, Positions.Start),
      checkForEmpty: (v: any) => !isDefined(v?.confidence_gte)
    };

    const matchesConfidenceOptions = {
      ...addRowClasses(options, Positions.End),
      checkForEmpty: (v: any) => !isDefined(v?.no_match)
    };

    return [this.getMatches(matchesOptions), this.getConfidence(matchesConfidenceOptions)];
  }

  getDirectMatch(options: SimpleFilterOptions, encode?: any, decode?: any): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: 'common.matches',
      i18n_placeholder: 'common.matches',
      anyItem: true,
      labeled: true,
      closeIcon: false,
      multiple: false,
      items: [
        { value: true, i18n_label: 'common.only_with_matches' },
        { value: false, i18n_label: 'common.only_without_matches' }
      ]
    };

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions, null, encode, decode) : configButtonGroup(resultOptions, encode, decode);
  }

  getDirectMatchWithConfidence(options: SimpleFilterOptions): IFormLayoutRow {
    const matchOptions = {
      ...addRowClasses(options, Positions.Start),
      checkForEmpty: (v: any) => !isDefined(v?.matched)
    };

    function encode(model: any, value: boolean) {
      model.matched = value;
      if (!value) delete model.confidence_gte;
    }

    function decode(model: any) {
      return model.matched;
    }

    const matchConfidenceOptions = {
      ...addRowClasses(options, Positions.End),
      checkForEmpty: (v: any) => !(v?.matched === true)
    };

    return [this.getDirectMatch(matchOptions, encode, decode), this.getConfidence(matchConfidenceOptions)];
  }

  getConfidence(options: SimpleFilterOptions) {
    return configureConfidence(options);
  }

  getEpisodeId(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'episode',
      i18n_label: 'common.episode_id',
      i18n_placeholder: 'common.enter_episode_id',
      inputAlphabet: '0123456789'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getEventId(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'id_in',
      i18n_label: 'common.event_id',
      i18n_placeholder: 'common.enter_event_id',
      multiple: true,
      inputAlphabet: '0123456789,'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  // EPISODES

  getEpisodeAllowedTypes(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'allowed_types',
      i18n_label: 'common.episode_allowed_types',
      i18n_placeholder: 'common.episode_allowed_types',
      anyItem: true,
      labeled: true,
      multiple: true,
      items: [
        { value: 'episode_open', i18n_label: 'common.episode_open' },
        { value: 'episode_event', i18n_label: 'common.episode_event' },
        { value: 'episode_close', i18n_label: 'common.episode_close' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  // HUMAN EPISODES

  getFaceMatches(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'no_face_match',
      i18n_label: 'common.face_matches',
      i18n_placeholder: 'common.face_matches',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'False', i18n_label: 'common.only_with_matches' },
        { value: 'True', i18n_label: 'common.only_without_matches' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getBodyMatches(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'no_body_match',
      i18n_label: 'common.body_matches',
      i18n_placeholder: 'common.body_matches',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'False', i18n_label: 'common.only_with_matches' },
        { value: 'True', i18n_label: 'common.only_without_matches' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getFaceDirectMatch(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'face_matched',
      i18n_label: 'common.face_matches',
      i18n_placeholder: 'common.face_matches',
      anyItem: true,
      labeled: true,
      multiple: false,
      items: [
        { value: true, i18n_label: 'common.only_with_matches' },
        { value: false, i18n_label: 'common.only_without_matches' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getBodyDirectMatch(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'body_matched',
      i18n_label: 'common.body_matches',
      i18n_placeholder: 'common.body_matches',
      anyItem: true,
      labeled: true,
      multiple: false,
      items: [
        { value: true, i18n_label: 'common.only_with_matches' },
        { value: false, i18n_label: 'common.only_without_matches' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getEpisodeIdInEpisodes(options: SimpleFilterOptions): IFormLayoutItem {
    return this.getEpisodeId({ ...options, path: 'id_in', multiple: true });
  }

  getEventsCount(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'events_count_gte',
      i18n_label: 'common.count_events',
      i18n_placeholder: 'common.count_events',
      anyItem: true,
      labeled: true,
      items: computeFieldItems(['1', '2', '3', '5', '10'])
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getAttributesDivider(options: SimpleFilterOptions): IFormLayoutItem | undefined {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: 'common.attributes'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? undefined : configureDivider(resultOptions);
  }

  getFaceAttributesDivider(options: SimpleFilterOptions): IFormLayoutItem | undefined {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: 'common.face_attributes'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? undefined : configureDivider(resultOptions);
  }

  getBodyAttributesDivider(options: SimpleFilterOptions): IFormLayoutItem | undefined {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: 'common.body_attributes'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? undefined : configureDivider(resultOptions);
  }

  // CAR EPISODES

  getCarMatches(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'no_car_match',
      i18n_label: 'common.matches',
      i18n_placeholder: 'common.matches',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'False', i18n_label: 'common.only_with_matches' },
        { value: 'True', i18n_label: 'common.only_without_matches' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCarDirectMatch(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'car_matched',
      i18n_label: 'common.matches',
      i18n_placeholder: 'common.matches',
      anyItem: true,
      labeled: true,
      multiple: false,
      items: [
        { value: true, i18n_label: 'common.only_with_matches' },
        { value: false, i18n_label: 'common.only_without_matches' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCarAttributesDivider(options: SimpleFilterOptions): IFormLayoutItem | undefined {
    const baseOptions: SimpleFilterOptions = {
      i18n_label: 'common.car_attributes'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? undefined : configureDivider(resultOptions);
  }

  // FACE EVENTS

  getBsType(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'bs_type',
      i18n_label: 'common.bs_type',
      i18n_placeholder: 'common.bs_type',
      anyItem: true,
      labeled: true,
      multiple: options.multiple,
      items: [
        { value: 'overall', i18n_label: 'common.bs_type_overall' },
        { value: 'realtime', i18n_label: 'common.bs_type_realtime' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  // ANNEX

  getAnnexAlarmStatus(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      name: 'status',
      i18n_label: 'annex_alarms.status',
      i18n_placeholder: 'annex_alarms.status',
      anyItem: true,
      items: [
        { value: 'new', i18n_label: 'annex_alarms.statuses.new' },
        { value: 'in_work', i18n_label: 'annex_alarms.statuses.in_work' },
        { value: 'completed', i18n_label: 'annex_alarms.statuses.completed' }
      ]
    };
    function decode(this: IFormContext): AnnexAlarmItemStatus {
      const model = this.model;

      if (typeof model.ack_value === 'undefined' && typeof model.has_comment === 'undefined') {
        return '';
      }

      if (model.ack_value === -1 && model.has_comment === false) {
        return 'new';
      }

      if (Array.isArray(model.ack_value) && model.ack_value.length == 2 && model.has_comment === false) {
        return 'in_work';
      }

      if (Array.isArray(model.ack_value) && model.ack_value.length == 2 && model.has_comment === true) {
        return 'completed';
      }

      return 'custom';
    }
    function encode(this: IFormContext, model: IFormModel, value: AnnexAlarmItemStatus) {
      delete this.model.ack_value;
      delete this.model.has_comment;

      if (value === '') {
        return;
      }

      if (value === 'new') {
        this.model.ack_value = -1;
        this.model.has_comment = false;
      }

      if (value === 'in_work') {
        this.model.ack_value = [0, 1];
        this.model.has_comment = false;
      }

      if (value === 'completed') {
        this.model.ack_value = [0, 1];
        this.model.has_comment = true;
      }
    }

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configButtonGroupNew(resultOptions, encode, decode) : configButtonGroup(resultOptions, encode, decode);
  }

  getAnnexAlarmStatusHelper(options: SimpleFilterOptions): IFormLayoutItem {
    return {
      name: 'status',
      component: AnnexAlarmStatusHelperIcon,
      classes: options.classes,
      props: {}
    };
  }

  getAnnexAlarmReaction(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'ack_value',
      i18n_label: 'annex_alarms.reaction',
      i18n_placeholder: 'annex_alarms.reaction',
      anyItem: true,
      labeled: true,
      multiple: true,
      items: [
        { value: -1, i18n_label: 'annex_alarms.reactions.none' },
        { value: 1, i18n_label: 'annex_alarms.reactions.acknowledged' },
        { value: 0, i18n_label: 'annex_alarms.reactions.rejected' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getAnnexAlarmComment(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'has_comment',
      i18n_label: 'annex_alarms.comments',
      i18n_placeholder: 'annex_alarms.comments',
      anyItem: true,
      labeled: true,
      items: [
        { value: true, i18n_label: 'annex_alarms.with_comments' },
        { value: false, i18n_label: 'annex_alarms.without_comments' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  // CARDS

  getHasFaceObjects(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'has_face_objects',
      i18n_label: 'common.has_face_objects',
      i18n_placeholder: 'common.has_face_objects',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'False', i18n_label: 'common.only_without_faces' },
        { value: 'True', i18n_label: 'common.only_with_faces' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getHasBodyObjects(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'has_body_objects',
      i18n_label: 'common.has_body_objects',
      i18n_placeholder: 'common.has_body_objects',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'False', i18n_label: 'common.only_without_bodies' },
        { value: 'True', i18n_label: 'common.only_with_bodies' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getHasCarObjects(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'has_car_objects',
      i18n_label: 'common.has_car_objects',
      i18n_placeholder: 'common.has_car_objects',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'False', i18n_label: 'common.only_without_cars' },
        { value: 'True', i18n_label: 'common.only_with_cars' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCarLicensePlateNumber(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'license_plate_number_contains',
      i18n_label: 'features.cars_license_plate_number',
      i18n_placeholder: 'common.enter_number_plate',
      i18n_tooltip: 'common.enter_number_plate_contains_description'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configLicensePlateInput(resultOptions);
  }

  getWatchLists(options: SimpleFilterOptions): IFormLayoutItem {
    return this.getMatchedLists({
      ...options,
      path: 'watch_lists',
      i18n_label: 'common.watch_lists',
      i18n_placeholder: 'common.watch_lists'
    });
  }

  getRecordIdContains(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'record_id',
      i18n_label: 'cases.record_id',
      i18n_placeholder: 'cases.record_id'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configCardInput(resultOptions);
  }

  getNameContains(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'name_contains',
      i18n_label: 'common.name_contains',
      i18n_placeholder: 'common.name_contains'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configCardInput(resultOptions);
  }

  getId(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'id_in',
      i18n_label: 'common.id',
      i18n_placeholder: 'common.id',
      multiple: true,
      inputAlphabet: '0123456789,'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getAlertRuleId(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'rule_id_in',
      i18n_label: 'alerts.id_alert_rule',
      i18n_placeholder: 'alerts.id_alert_rule',
      multiple: true,
      inputAlphabet: '0123456789,'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getPhotoId(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'case_photo_in',
      i18n_label: 'common.photo_id',
      i18n_placeholder: 'common.photo_id',
      multiple: true
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getVideoId(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'video_archive_in',
      i18n_label: 'common.video_id',
      i18n_placeholder: 'common.video_id',
      multiple: true,
      inputAlphabet: '0123456789,'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  // Clusters

  getHasCard(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'has_card',
      i18n_label: 'common.matches',
      i18n_placeholder: 'common.matches',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'True', i18n_label: 'common.only_with_matches' },
        { value: 'False', i18n_label: 'common.only_without_matches' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getHasCaseParticipants(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'has_caseparticipants',
      i18n_label: 'common.participant',
      i18n_placeholder: 'common.participant',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'True', i18n_label: 'common.only_with_participant' },
        { value: 'False', i18n_label: 'common.only_without_participant' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  // PARTICIPANTS

  getCaseRole(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'role_in',
      i18n_label: 'cases.role',
      i18n_placeholder: 'cases.role',
      multiple: true,
      anyItem: true,
      items: [
        { value: RoleEnum.VICTIM, i18n_label: 'cases.victim' },
        { value: RoleEnum.WITNESS, i18n_label: 'cases.witness' },
        { value: RoleEnum.SUSPECT, i18n_label: 'cases.suspect' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCaseNameContains(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'name_contains',
      i18n_label: 'cases.participant_name',
      i18n_placeholder: 'cases.enter_participant_name'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getCaseDateTime(options: SimpleFilterOptions): IFormLayoutItem | undefined {
    return this.getDateTimeRange({ ...options, i18n_label: 'cases.date_time_participant' });
  }

  getCaseIn(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'case_in',
      i18n_label: 'cases.case_id',
      i18n_placeholder: 'cases.enter_case_id',
      multiple: true
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getContactThreshold(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'contact_threshold_seconds',
      i18n_label: 'clusters.contact_threshold_seconds',
      i18n_tooltip: 'clusters.contact_threshold_seconds_tooltip',
      anyItem: false,
      labeled: true,
      closeIcon: false,
      items: [
        { value: 1, label: '1s' },
        { value: 3, label: '3s' },
        { value: 5, label: '5s' },
        { value: 10, label: '10s' },
        { value: 20, label: '20s' },
        { value: 30, label: '30s' },
        { value: 60, label: '60s' },
        { value: 120, label: '120s' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getLimit(options: SimpleFilterOptions, maximum?: number): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      items: [
        { value: 10, label: '10' },
        { value: 20, label: '20' },
        { value: 50, label: '50' },
        { value: 100, label: '100' },
        { value: 200, label: '200' },
        { value: 300, label: '300' },
        { value: 500, label: '500' },
        { value: 750, label: '750' },
        { value: 1000, label: '1000' }
      ],
      path: 'limit',
      i18n_label: 'common.limit',
      i18n_placeholder: 'common.limit'
    };
    if (maximum) {
      let items = baseOptions.items!.filter((v) => v.value < maximum);
      items.push({ value: maximum, label: String(maximum) });
      baseOptions.items = items;
    }
    const resultOptions = Object.assign(baseOptions, options);

    const result = options?.small ? configSelectSmall({ ...resultOptions, clearIcon: false, labeled: true }) : configSelect(resultOptions);

    result.decode = function (this: IFormContext) {
      return Number(this.model.limit);
    };

    return result;
  }

  getUseLastEvent(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'use_last_event',
      i18n_label: 'clusters.use_last_event',
      i18n_tooltip: 'clusters.use_last_event_tooltip'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configCheckbox(resultOptions) : configCheckbox(resultOptions);
  }

  getActiveStatus(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'active',
      i18n_label: 'common.active_camera',
      i18n_placeholder: 'common.active_camera',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'True', i18n_label: 'common.only_active' },
        { value: 'False', i18n_label: 'common.only_inactive' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCameraLocation(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'has_coordinates',
      i18n_label: 'ds.location',
      i18n_placeholder: 'ds.location',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'False', i18n_label: 'ds.location_none' },
        { value: 'True', i18n_label: 'ds.location_area' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCaseMatchType(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'case_match_type',
      i18n_label: 'common.match_type',
      // wtf? i18n_placeholder: 'common.active',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'related', i18n_label: 'common.match_type_related' },
        { value: 'matched', i18n_label: 'common.match_type_matched' },
        { value: 'matched_or_related', i18n_label: 'common.match_type_matched_or_related' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCameraStatus(options: SimpleFilterOptions): IFormLayoutItem {
    const codes: CameraStatusColorCode[] = ['gray', 'green', 'red', 'yellow'];
    const items: any[] = codes.map((code) => ({ color: code, i18n_label: `ds.${code}`, value: code }));

    const baseOptions: SimpleFilterOptions = {
      path: 'state_color',
      i18n_label: 'common.working_status',
      i18n_placeholder: 'common.working_status',
      anyItem: true,
      labeled: true,
      items
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCounterStatus(options: SimpleFilterOptions): IFormLayoutItem {
    const codes: CameraStatusColorCode[] = ['gray', 'green', 'red', 'yellow'];
    const items: any[] = codes.map((code) => ({ color: code, i18n_label: `ds.${code}`, value: code }));

    const baseOptions: SimpleFilterOptions = {
      path: 'color',
      i18n_label: 'common.status',
      i18n_placeholder: 'common.status',
      anyItem: true,
      multiple: true,
      items
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getDeviceUIDInput(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'device_uid',
      i18n_label: 'audit_logs.device_uid',
      i18n_placeholder: 'audit_logs.input_device_uid',
      multiple: false
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getAuditObjectTypeSelect(options: SimpleFilterOptions): IFormLayoutItem {
    const loadItems = async () =>
      (await fetchAuditLogsObjectTypes()).map((item) => {
        return { label: languageModule.getTranslatedToken(`audit_logs.objectTypes.${item}`, 'f'), value: item };
      });

    const baseOptions: SimpleFilterOptions = {
      path: 'object_type',
      i18n_label: 'audit_logs.object_type',
      i18n_placeholder: 'audit_logs.select_object',
      multiple: true,
      loadItems: loadItems
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions);
  }

  getAuditActionTypeSelect(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'action_type',
      i18n_label: 'audit_logs.action_type',
      i18n_placeholder: 'audit_logs.select_action',
      multiple: false
    };
    const resultOptions = Object.assign(baseOptions, options);
    const layoutItem = options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions);
    const layoutItemProps = layoutItem.props;
    const layoutItemPropsFunction = (filter: AuditLogsFilter) => {
      const loadItems = async () => {
        const items = await fetchAuditLogsObjectActionsMap();
        const matchedItems = matchAuditLogsObjectTypesWithActions(items, []);
        return matchedItems.map((action) => {
          return { label: languageModule.getTranslatedToken(`audit_logs.actionTypes.${action}`, 'f'), value: action };
        });
      };
      return { ...layoutItemProps, loadItems };
    };
    layoutItem.props = layoutItemPropsFunction;
    return layoutItem;
  }

  getObjectIdInput(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'object_id',
      i18n_label: 'audit_logs.object_id',
      i18n_placeholder: 'audit_logs.input_object_id'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configLicensePlateInput(resultOptions);
  }

  getUser(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'user',
      i18n_label: 'common.user',
      i18n_placeholder: 'common.user'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configUserSelectSmall(resultOptions) : configUserSelect(resultOptions);
  }

  getUserByName(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'user',
      i18n_label: 'common.user',
      i18n_placeholder: 'common.user',
      items: dataModule.usersModule.items.map((item: User) => ({ value: item?.name, label: item?.name + ` (${item.real_name})` })),
      multiple: false,
      filtered: true,
      selectonly: false
    };

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configSelect(resultOptions);
  }

  getUserLogin(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'user_login',
      i18n_label: 'audit_logs.user',
      i18n_placeholder: 'audit_logs.input_user'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configLicensePlateInput(resultOptions);
  }

  getUserRealName(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'user',
      i18n_label: 'common.user',
      i18n_placeholder: 'common.user'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configUserRealNameSelectSmall(resultOptions) : configUserRealNameSelect(resultOptions);
  }

  getRequestIPAddress(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'request_ip',
      i18n_label: 'audit_logs.request_ip',
      i18n_placeholder: 'audit_logs.input_request_ip'
    };
    const resultOptions = Object.assign(baseOptions, options);
    const layoutItem = options?.small ? configMultipleInputSmall(resultOptions) : configLicensePlateInput(resultOptions);
    layoutItem.validators = [
      {
        handler: (context: any) => {
          return isStringWithValue(context.model.request_ip)
            ? isValidIpV4Address(context.model.request_ip) || isValidIpV6Address(context.model.request_ip)
            : true;
        },
        i18n_message: 'audit_logs.request_ip_is_invalid'
      }
    ];
    return layoutItem;
  }

  getUUIDInput(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'uuid_contains',
      i18n_label: 'common.uuid'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getReasonInput(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'reason_contains',
      i18n_label: 'common.reason'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  // LINES
  getBackwardLineCrossing(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'backward_line_crossing',
      i18n_label: 'lines.has_backward_line_crossing',
      i18n_placeholder: 'lines.has_backward_line_crossing',
      anyItem: true,
      labeled: true,
      closeIcon: false,
      items: [
        { value: 'True', i18n_label: 'lines.with_backward_line_crossing' },
        { value: 'False', i18n_label: 'lines.without_backward_line_crossing' }
      ]
    };

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getBackwardLineCrossingWebhooks(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions = {
      items: [
        { value: true, i18n_label: 'lines.with_backward_line_crossing' },
        { value: false, i18n_label: 'lines.without_backward_line_crossing' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return this.getBackwardLineCrossing(resultOptions);
  }

  getLineCrossDirection(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'line_crossing_direction',
      i18n_label: 'lines.line_crossing_direction',
      i18n_placeholder: 'lines.line_crossing_direction',
      anyItem: true,
      multiple: true,
      closeIcon: false,
      items: [
        { value: LineDirections.None, i18n_label: 'lines.line_crossing_direction_none' },
        { value: LineDirections.BA, i18n_label: 'lines.line_crossing_direction_b_a' },
        { value: LineDirections.AB, i18n_label: 'lines.line_crossing_direction_a_b' }
      ]
    };

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getLineId(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'line',
      i18n_label: 'lines.line',
      i18n_placeholder: 'lines.enter_line_name',
      multiple: true,
      ...generateLoadSearchMethodsByLVM(dataModule.linesModule),
    };

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectAsTagsSmall(resultOptions) : configSelectAsTags(resultOptions);
  }

  // COUNTER

  getDistanceType(options: SimpleFilterOptions = {}) {
    const baseOptions: SimpleFilterOptions = {
      path: 'detect_proximity',
      i18n_label: 'counters.distance',
      i18n_placeholder: 'counters.distance',
      anyItem: true,
      labeled: true,
      items: [
        { value: 'True', i18n_label: 'counters.with_detect_proximity' },
        { value: 'False', i18n_label: 'counters.without_detect_proximity' }
      ]
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getCountFaces(options: SimpleFilterOptions = {}) {
    const baseOptions: SimpleFilterOptions = {
      name: 'faces_count',
      i18n_label: 'counters.count_faces',
      i18n_placeholder: 'counters.count_faces'
    };

    const resultOptions = Object.assign(baseOptions, options);
    const gteObject = {
      fieldName: 'faces_count_gte',
      min: 0,
      max: 1000
    };
    const lteObject = {
      fieldName: 'faces_count_lte',
      min: 0,
      max: 1000
    };
    return configureRange(resultOptions, gteObject, lteObject);
  }

  getCountSilhouettes(options: SimpleFilterOptions = {}) {
    const baseOptions: SimpleFilterOptions = {
      name: 'silhouettes_count',
      i18n_label: 'counters.count_silhouettes',
      i18n_placeholder: 'counters.count_silhouettes'
    };

    const resultOptions = Object.assign(baseOptions, options);
    const gteObject = {
      fieldName: 'silhouettes_count_gte',
      min: 0,
      max: 1000
    };
    const lteObject = {
      fieldName: 'silhouettes_count_lte',
      min: 0,
      max: 1000
    };
    return configureRange(resultOptions, gteObject, lteObject);
  }

  getCountCars(options: SimpleFilterOptions = {}) {
    const baseOptions: SimpleFilterOptions = {
      name: 'cars_count',
      i18n_label: 'counters.count_cars',
      i18n_placeholder: 'counters.count_cars'
    };

    const resultOptions = Object.assign(baseOptions, options);
    const gteObject = {
      fieldName: 'cars_count_gte',
      min: 0,
      max: 1000
    };
    const lteObject = {
      fieldName: 'cars_count_lte',
      min: 0,
      max: 1000
    };
    return configureRange(resultOptions, gteObject, lteObject);
  }

  getMinimumDistance(options: SimpleFilterOptions = {}) {
    const baseOptions: SimpleFilterOptions = {
      name: 'min_distance',
      i18n_label: 'counters.min_distance',
      i18n_placeholder: 'counters.min_distance'
    };

    const resultOptions = Object.assign(baseOptions, options);
    const gteObject = {
      fieldName: 'proximity_min_gte',
      min: 0,
      max: 1000
    };
    const lteObject = {
      fieldName: 'proximity_min_lte',
      min: 0,
      max: 1000
    };

    return configureRange(resultOptions, gteObject, lteObject);
  }

  getMaximumDistance(options: SimpleFilterOptions = {}) {
    const baseOptions: SimpleFilterOptions = {
      name: 'max_distance',
      i18n_label: 'counters.max_distance',
      i18n_placeholder: 'counters.max_distance'
    };

    const resultOptions = Object.assign(baseOptions, options);
    const gteObject = {
      fieldName: 'proximity_max_gte',
      min: 0,
      max: 1000
    };
    const lteObject = {
      fieldName: 'proximity_max_lte',
      min: 0,
      max: 1000
    };

    return configureRange(resultOptions, gteObject, lteObject);
  }

  getAverageDistance(options: SimpleFilterOptions = {}) {
    const baseOptions: SimpleFilterOptions = {
      name: 'avg_distance',
      i18n_label: 'counters.avg_distance',
      i18n_placeholder: 'counters.avg_distance'
    };

    const resultOptions = Object.assign(baseOptions, options);
    const gteObject = {
      fieldName: 'proximity_avg_gte',
      min: 0,
      max: 1000
    };
    const lteObject = {
      fieldName: 'proximity_avg_lte',
      min: 0,
      max: 1000
    };

    return configureRange(resultOptions, gteObject, lteObject);
  }

  getHasError(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'error',
      i18n_label: 'common.has_error',
      i18n_placeholder: 'common.has_error',
      anyItem: true,
      labeled: true,
      closeIcon: false,
      items: [
        { value: 'True', i18n_label: 'common.with_error' },
        { value: 'False', i18n_label: 'common.without_error' }
      ]
    };

    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configSelectSmall(resultOptions) : configButtonGroup(resultOptions);
  }

  getLatitudeLte(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'latitude_lte',
      i18n_label: 'common.latitude_lte',
      i18n_placeholder: 'common.latitude_lte'
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getLatitudeGte(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'latitude_gte',
      i18n_label: 'common.latitude_gte',
      i18n_placeholder: 'common.latitude_gte',
      readonly: true
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getLongitudeLte(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'longitude_lte',
      i18n_label: 'common.longitude_lte',
      i18n_placeholder: 'common.longitude_lte',
      readonly: true
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }

  getLongitudeGte(options: SimpleFilterOptions): IFormLayoutItem {
    const baseOptions: SimpleFilterOptions = {
      path: 'longitude_gte',
      i18n_label: 'common.longitude_gte',
      i18n_placeholder: 'common.longitude_gte',
      readonly: true
    };
    const resultOptions = Object.assign(baseOptions, options);
    return options?.small ? configMultipleInputSmall(resultOptions) : configInput(resultOptions);
  }
}

export const commonFilterSchemaModule = new CommonFilterSchemaModule();
